import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { UserService } from '@plantefeve/common/api/user.service';
import { Roles } from '@plantefeve/common/constants/enums/roles.enum';
import { DisplayValueModel } from '@plantefeve/common/models/display-value.model';
import { ChangePasswordDialogData, UpsertUser, User } from '@plantefeve/common/models/users.model';
import { AuthorisationService } from '@plantefeve/common/services/authorisation.service';
import { DialogButtonsComponent } from '@plantefeve/common/shared/components/dialog-buttons/dialog-buttons.component';
import { HeaderComponent } from '@plantefeve/common/shared/components/header/header.component';
import { LoaderComponent } from '@plantefeve/common/shared/components/loader/loader.component';
import { MultiSelectComponent } from '@plantefeve/common/shared/components/multi-select/multi-select.component';
import { TextInputComponent } from '@plantefeve/common/shared/components/text-input/text-input.component';
import { ButtonOptions } from '@plantefeve/common/shared/models/header.model';
import { ConfirmationData, ConfirmService } from '@plantefeve/common/shared/services/confirmation.service';
import { passwordValidator } from '@plantefeve/common/shared/services/password-validator.service';
import { ToastService } from '@plantefeve/common/shared/services/toast.service';
import { PrimeIcons } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { DialogService } from 'primeng/dynamicdialog';
import { FloatLabel } from 'primeng/floatlabel';
import { FluidModule } from 'primeng/fluid';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { PasswordModule } from 'primeng/password';
import { debounceTime, distinctUntilChanged, EMPTY, filter, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { ChangePasswordDialogComponent } from '../change-password-dialog/change-password-dialog.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'igx-user-detail',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CheckboxModule,
    InputTextModule,
    ButtonModule,
    DividerModule,
    FluidModule,
    LoaderComponent,
    PanelModule,
    HeaderComponent,
    CardModule,
    TextInputComponent,
    MultiSelectComponent,
    PasswordModule,
    FloatLabel,
    TranslatePipe,
  ],
  templateUrl: './user-detail.component.html',
  styleUrl: './user-detail.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UserDetailComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _formBuilder = inject(NonNullableFormBuilder);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _dialog = inject(DialogService);
  private readonly _confirm = inject(ConfirmService);
  private readonly _authorisationService = inject(AuthorisationService);
  private readonly _authService = inject(AuthService);
  private readonly _toastService = inject(ToastService);
  private readonly _translateService = inject(TranslateService);

  public formGroup = this._formBuilder.group(
    {
      firstName: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(250)]),
      lastName: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(250)]),
      email: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(250), Validators.email]),
      password: new FormControl<string | null>(null),
      roles: new FormControl<string[] | null>(null, [Validators.required, Validators.minLength(1)]),
      blocked: new FormControl<boolean | null>(false),
    },
    { validators: this.rolesValidator() }
  );

  public passwordRepeat = new FormControl<string | null>(null, [passwordValidator()]);

  public title = signal<string>(this._translateService.instant('Nieuwe gebruiker aanmaken'));
  public userId = signal<string>(null);
  public isLoading = signal<boolean>(false);
  public isNewUser = computed(() => this.userId() == null);
  public roles = signal<DisplayValueModel[]>(undefined);
  public isAdmin = signal<boolean>(false);
  public isOwnAccount: boolean = false;

  public headerButtons = signal<ButtonOptions[]>([
    {
      label: this._translateService.instant('Opslaan'),
      click: () => this.onSave(false),
      icon: PrimeIcons.SAVE,
    },
    {
      label: this._translateService.instant('Opslaan en sluiten'),
      click: () => this.onSave(true),
      icon: PrimeIcons.SAVE,
      variant: 'outlined',
    },
    {
      label: this._translateService.instant('Annuleren'),
      click: () => this.onCancel(),
      icon: PrimeIcons.TIMES,
      severity: 'danger',
    },
  ]);

  public ngOnInit(): void {
    const userId$ = this._route.paramMap.pipe(
      takeUntilDestroyed(this._destroyRef),
      map((paramMap) => paramMap.get('userId')),
      switchMap((userId) => {
        if (userId === 'new') {
          return of(null);
        }
        if (userId) {
          return of(userId);
        } else {
          this.isOwnAccount = true;
          return this._authService.user$.pipe(map((u) => u.sub));
        }
      }),
      tap((res) => this.userId.update(() => res)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    userId$
      .pipe(
        filter((userId) => !!userId),
        tap(() => this.isLoading.update(() => true)),
        switchMap((userId: string) => this.userService.getUser(userId)),
        tap((user) => {
          this.patchFormGroup(user);
          this.isLoading.update(() => false);
          this.title.update(() => `${user.fullName} ` + this._translateService.instant('aanpassen'));

          this.setUserBlockedLabel(user.blocked);
        })
      )
      .subscribe();

    userId$
      .pipe(
        switchMap(() =>
          this._authorisationService.isInRole(Roles.Administrator).pipe(
            tap((isAdmin) => this.isAdmin.set(isAdmin)),
            tap(() => this.setHeaderNavigation())
          )
        )
      )
      .subscribe();

    this.userService
      .getRoles()
      .pipe(
        map((roles) => roles.map((r) => ({ label: r.description, value: r.id }) as DisplayValueModel)),
        tap((roles) => this.roles.update(() => roles))
      )
      .subscribe();

    userId$
      .pipe(
        filter((userId) => !!userId),
        switchMap(() => this.formGroup.valueChanges),
        filter((form) => !!form.firstName && !!form.lastName),
        distinctUntilChanged(),
        debounceTime(200),
        tap((form) =>
          this.title.update(() => `${form.firstName} ${form.lastName} ` + this._translateService.instant('aanpassen'))
        )
      )
      .subscribe();
  }

  public onCancel(): void {
    if (!this.isOwnAccount && this.isAdmin()) {
      this._router.navigate(['users', 'backoffice']);
    } else {
      this._router.navigate(['']);
    }
  }

  public onSave(close: boolean): void {
    this.formGroup.markAllAsTouched();

    if (!this.passwordValid()) {
      return;
    }

    if (this.formGroup.invalid) {
      this._toastService.showError(
        this._translateService.instant('Gelieve alle verplichte velden (*) in te vullen.'),
        this._translateService.instant('Validatiefout')
      );
      return;
    }

    const upsertUser = this.formGroup.value as UpsertUser;

    const user$ = !!this.userId()
      ? this.userService.update(this.userId(), upsertUser)
      : this.userService.create(upsertUser);

    user$
      .pipe(
        takeUntilDestroyed(this._destroyRef),

        // check of de huidige user admin is
        switchMap((user) =>
          this._authorisationService.isInRole(Roles.Administrator).pipe(map((isAdmin) => ({ user, isAdmin })))
        ),
        switchMap(({ user, isAdmin }) => {
          if (isAdmin) {
            return this.userService
              .updateUserRoles(user.id, this.formGroup.controls.roles.value)
              .pipe(map((roles) => ({ user, isAdmin, roles })));
          } else {
            let roles = [];
            return of({ user, isAdmin, roles });
          }
        }),
        tap(({ user, isAdmin, roles }) => {
          this.userId.set(user.id);
          if (isAdmin && !this.isOwnAccount) {
            if (close) {
              this._router.navigate(['users', 'backoffice']);
              return;
            } else if (!!this.userId()) {
              this._router.navigate(['users', user.id, 'details']);
            }
            user.roles = roles;
            this.patchFormGroup(user);
          } else {
            if (close) {
              this._router.navigate(['']);
            } else {
              this._router.navigate(['users', 'details']);
            }
          }
        })
      )
      .subscribe();
  }

  public onChangePassword(): void {
    this._dialog
      .open(ChangePasswordDialogComponent, {
        header: this._translateService.instant(`Wachtwoord aanpassen`),
        modal: true,
        maximizable: true,
        closable: true,
        resizable: true,
        data: {} as ChangePasswordDialogData,
        templates: {
          footer: DialogButtonsComponent,
        },
      })
      .onClose.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((password) =>
        typeof password === 'string' ? this.userService.changePassword(this.userId(), password).subscribe() : EMPTY
      );
  }

  public onBlockUser(): void {
    this._confirm
      .confirm({
        acceptIcon: 'pi-trash',
        severity: 'danger',
        acceptLabel: of(
          this._translateService.instant(this.formGroup.controls.blocked.value ? 'Deblokkeer' : 'Blokkeer')
        ),
        header: 'Gebruiker blokkeren',
        message: of(
          this._translateService.instant(
            `Weet je zeker dat dit account wilt ${this.formGroup.controls.blocked.value ? 'deblokkeren' : 'blokkeren'}?`
          )
        ),
      } as ConfirmationData)
      .pipe(
        filter((response) => response),
        map(() => this.formGroup.controls.blocked.value),
        switchMap((blocked) =>
          this.userService.blockUser(this.userId(), !blocked).pipe(
            tap((blocked) => {
              this.formGroup.controls.blocked.patchValue(blocked);
              this.setUserBlockedLabel(blocked);
            })
          )
        )
      )
      .subscribe();
  }

  private passwordValid() {
    if (this.userId()) {
      return true;
    }
    if (this.formGroup.controls.password.value !== this.passwordRepeat.value) {
      this.passwordRepeat.setErrors({ passwordMismatch: true });
    }
    if (this.passwordRepeat.hasError('passwordWeak')) {
      this._toastService.showError(
        this._translateService.instant('Kies een sterker wachtwoord'),
        this._translateService.instant('Validatiefout')
      );
    }
    if (this.passwordRepeat.hasError('passwordTooShort')) {
      this._toastService.showError(
        this._translateService.instant('Wachtwoord is te kort'),
        this._translateService.instant('Validatiefout')
      );
    }
    if (this.passwordRepeat.hasError('passwordMismatch')) {
      this._toastService.showError(
        this._translateService.instant('Wachtwoorden komen niet overeen.'),
        this._translateService.instant('Validatiefout')
      );
    }

    return this.passwordRepeat.valid;
  }

  private setHeaderNavigation(): void {
    if (!this.isNewUser()) {
      this.headerButtons().splice(this.headerButtons.length - 1, 0, {
        label: this._translateService.instant('Verander wachtwoord'),
        click: () => this.onChangePassword(),
        icon: PrimeIcons.KEY,
        variant: 'outlined',
        disabled: this.isNewUser(),
      });
    }

    if (this.isAdmin() && !this.isOwnAccount && !!this.userId()) {
      this.headerButtons().splice(this.headerButtons().length - 1, 0, {
        label: 'Blokkeren',
        click: () => this.onBlockUser(),
        icon: PrimeIcons.BAN,
        severity: 'danger',
        variant: 'outlined',
      });
    }
  }

  private setUserBlockedLabel(blocked: boolean) {
    const btn = this.headerButtons().find((b) => b.label === 'Blokkeren' || b.label == 'Deblokkeren');
    if (btn) {
      btn.label = blocked ? 'Deblokkeren' : 'Blokkeren';
      btn.variant = blocked ? null : 'outlined';
    }
  }

  private patchFormGroup(user: User): void {
    this.formGroup.patchValue({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      blocked: user.blocked,
      roles: user.roles.map((r) => r.id),
    });
  }

  private rolesValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const rolesControl = group.get('roles');

      if (!this.isAdmin || !rolesControl?.value) {
        return null;
      }

      if (rolesControl.value.length == 0) {
        rolesControl.setErrors({ minlength: true });
      } else {
        if (rolesControl.errors) {
          delete rolesControl.errors['minlength'];
          if (!Object.keys(rolesControl.errors).length) {
            rolesControl.setErrors(null);
          }
        }
      }

      return null;
    };
  }
}
